// Content data for all inner pages — source of truth mapped to the brand document.

export const SOLUTIONS = {
  "ai-product-engineering": {
    slug: "ai-product-engineering",
    eyebrow: "AI Product Engineering",
    h1: "Turn AI Ideas Into Production-Ready Products",
    intro:
      "Move beyond disconnected experiments and impressive demos. Techovarya helps SaaS companies and growing businesses design, build, integrate, and scale AI capabilities that work inside real products and operational workflows. From intelligent copilots and document systems to AI-powered product features and natural-language experiences, we engineer for production reality.",
    primaryCta: "Discuss Your AI Product",
    secondaryCta: "Explore AI Use Cases",
    finalCtaHeading: "Move Your AI Initiative Into Production",
    finalCtaCopy:
      "Bring us the use case, prototype, product challenge, or architecture question. We will help you define the next practical step.",
    finalCta: "Book an AI Product Strategy Call",
    sections: [
      {
        type: "prose",
        eyebrow: "The Gap",
        heading:
          "The Distance Between an AI Demo and a Reliable Product Is Engineering",
        body: "Connecting an LLM API is relatively easy. Building an AI capability that works with your data, respects permissions, integrates with existing systems, handles failure, controls cost, supports evaluation, and delivers a reliable user experience is a different challenge. We help close that gap.",
      },
      {
        type: "list",
        eyebrow: "Problems We Solve",
        heading: "Where We Typically Help",
        items: [
          "You have an AI concept but no clear production architecture.",
          "Your customers are asking for AI capabilities.",
          "Your team has built a prototype that cannot yet support production use.",
          "AI responses lack business context.",
          "Your internal knowledge is fragmented across documents and systems.",
          "You need to connect multiple models, APIs, and workflows.",
          "You need better control over latency and cost.",
          "Your engineering team needs specialized AI capability.",
          "You need to introduce AI without destabilizing the existing product.",
        ],
      },
      {
        type: "capabilities",
        eyebrow: "What We Build",
        heading: "AI Capabilities Designed Around Real Use Cases",
        items: [
          ["AI Copilots", "Context-aware assistants embedded inside existing products and workflows."],
          ["Intelligent Search", "Natural-language discovery across structured and unstructured information."],
          ["Document Intelligence", "Extract, classify, summarize, validate, and route information from complex documents."],
          ["AI-Powered SaaS Features", "Introduce intelligent functionality directly into customer-facing software products."],
          ["Natural-Language Analytics", "Enable users to explore business information through conversational interfaces."],
          ["Knowledge Assistants", "Connect internal knowledge with accessible, context-aware AI experiences."],
          ["AI Workflow Automation", "Combine models, tools, APIs, business rules, and human approvals."],
          ["Multi-Model Systems", "Select and orchestrate models based on task, performance, latency, and cost."],
        ],
      },
      {
        type: "steps",
        eyebrow: "Our Approach",
        heading: "From Opportunity to Production",
        items: [
          ["Discover", "Define the user, workflow, business problem, data context, constraints, and expected value."],
          ["Architect", "Design the model strategy, retrieval approach, application architecture, integrations, security boundaries, and evaluation framework."],
          ["Build", "Develop the product experience, backend services, orchestration, data pipelines, integrations, and interfaces."],
          ["Evaluate", "Test quality, reliability, latency, cost, edge cases, and business-specific performance."],
          ["Deploy", "Introduce observability, feedback mechanisms, security controls, and production monitoring."],
          ["Improve", "Continuously optimize quality, workflows, model selection, cost, and user experience."],
        ],
      },
      {
        type: "list",
        eyebrow: "Ideal Clients",
        heading: "Built for Teams Moving Beyond AI Experimentation",
        items: [
          "SaaS companies adding intelligent capabilities",
          "Technology companies launching AI-native products",
          "Enterprises automating knowledge-intensive workflows",
          "Product teams moving prototypes into production",
          "Businesses with valuable proprietary data",
          "Companies requiring AI integration across existing systems",
        ],
      },
    ],
  },

  "ai-agents-rag": {
    slug: "ai-agents-rag",
    eyebrow: "AI Agents & RAG Systems",
    h1: "Build AI Systems That Understand Your Business",
    intro:
      "Generic AI understands general information. Useful business AI must work with your documents, systems, permissions, workflows, customers, and operational context. Techovarya builds AI agents and retrieval systems that connect language models with the knowledge and tools required to perform meaningful work.",
    primaryCta: "Explore Your AI Use Case",
    finalCtaHeading: "Turn Business Knowledge Into an Intelligent System",
    finalCtaCopy:
      "Tell us where your information lives, who needs access to it, and what work you want AI to support.",
    finalCta: "Discuss Your RAG or Agent Use Case",
    sections: [
      {
        type: "capabilities",
        eyebrow: "What We Build",
        heading: "Intelligent Systems Connected to Real Business Context",
        items: [
          ["Enterprise Knowledge Assistants", "Answer employee questions from your internal knowledge base with citations and permission-aware retrieval."],
          ["Customer Support Agents", "Handle routine tickets and hand off to humans with full context."],
          ["Sales Research Agents", "Enrich accounts, prospect intelligence, and outreach research."],
          ["Document Processing Agents", "Extract, classify, and route information from complex documents."],
          ["Internal Operations Copilots", "In-app assistants for CRMs, ERPs, and internal platforms."],
          ["Multi-Step Workflow Agents", "Chain reasoning, retrieval, and tool calls across long-running tasks."],
          ["RAG-Based Search Systems", "Natural-language search over private data with source grounding."],
          ["AI Analytics Assistants", "Conversational interfaces for business intelligence."],
          ["Tool-Using Agents", "Agents that call APIs, update systems, and request human approvals."],
          ["Human-in-the-Loop Workflows", "AI decisions with review gates for high-risk actions."],
        ],
      },
      {
        type: "prose",
        eyebrow: "RAG Engineering",
        heading: "RAG Is More Than Connecting a Vector Database",
        body: "A useful retrieval system depends on how information is ingested, segmented, indexed, filtered, retrieved, ranked, secured, and evaluated.",
      },
      {
        type: "list",
        heading: "Our RAG Work Can Include",
        items: [
          "Document ingestion pipelines",
          "Data normalization",
          "Chunking strategies",
          "Embedding architecture",
          "Vector databases",
          "Hybrid search",
          "Metadata filtering",
          "Reranking",
          "Query transformation",
          "Permission-aware retrieval",
          "Source grounding",
          "Citation experiences",
          "Evaluation pipelines",
          "Observability",
          "Feedback loops",
        ],
      },
      {
        type: "prose",
        eyebrow: "Agent Engineering",
        heading: "Build Agents That Can Work Across Tools and Workflows",
        body: "We design AI systems that can reason across a defined task, access approved tools, retrieve relevant context, call APIs, update systems, request human approval, and continue multi-step workflows.",
      },
      {
        type: "list",
        heading: "Potential Applications",
        items: [
          "Sales research",
          "Support operations",
          "Document review",
          "Internal knowledge access",
          "Data enrichment",
          "Workflow coordination",
          "Operational reporting",
          "Customer onboarding",
          "Research assistance",
          "Back-office automation",
        ],
      },
      {
        type: "list",
        eyebrow: "Security & Control",
        heading: "Business AI Requires Boundaries",
        items: [
          "Authentication",
          "Authorization",
          "Role-based access",
          "Tenant isolation",
          "Permission-aware retrieval",
          "Sensitive data handling",
          "Tool restrictions",
          "Human approvals",
          "Auditability",
          "Source visibility",
          "Model fallback",
          "Failure handling",
        ],
      },
    ],
  },

  "saas-product-development": {
    slug: "saas-product-development",
    eyebrow: "SaaS Product Development",
    h1: "Build SaaS Products Designed to Scale Beyond the MVP",
    intro:
      "Launching quickly matters. But the engineering decisions made during early product development can shape years of future cost, complexity, and opportunity. Techovarya helps founders and product teams architect, build, launch, and evolve SaaS platforms with long-term product growth in mind.",
    primaryCta: "Discuss Your SaaS Product",
    finalCtaHeading: "Build the Next Stage of Your SaaS Product",
    finalCtaCopy: "Bring us your product context, roadmap, and constraints — we will help identify the right engineering path forward.",
    finalCta: "Discuss Your Product Roadmap",
    sections: [
      {
        type: "list",
        eyebrow: "What We Build",
        heading: "From Focused SaaS Products to Complex Multi-Tenant Platforms",
        items: [
          "B2B SaaS",
          "Vertical SaaS",
          "AI-Powered SaaS",
          "Analytics Platforms",
          "CRM Systems",
          "ERP Platforms",
          "Workflow Products",
          "Subscription Platforms",
          "Marketplaces",
          "Internal Platforms",
          "Customer Portals",
          "Data-Intensive Applications",
        ],
      },
      {
        type: "list",
        eyebrow: "Core Capabilities",
        heading: "The Engineering Behind a Scalable SaaS Product",
        items: [
          "Multi-Tenant Architecture",
          "Role-Based Access Control",
          "Subscription Billing",
          "Usage-Based Billing",
          "User Management",
          "Admin Platforms",
          "Dashboards",
          "Analytics",
          "Notifications",
          "Audit Logs",
          "API Integrations",
          "Data Architecture",
          "Cloud Deployment",
          "CI/CD",
          "Observability",
          "Search",
          "AI Capabilities",
        ],
      },
      {
        type: "capabilities",
        eyebrow: "Where We Enter",
        heading: "We Can Join at Different Product Stages",
        items: [
          ["New Product", "Turn a validated opportunity into a structured product and engineering roadmap."],
          ["MVP to Scale", "Strengthen architecture as customer demand and product complexity increase."],
          ["New Product Line", "Build a new SaaS capability alongside an existing business."],
          ["Platform Rebuild", "Replace critical parts of an aging product through a controlled engineering strategy."],
          ["AI Expansion", "Introduce AI capabilities into an existing SaaS workflow."],
          ["Capacity Expansion", "Add engineering capability to accelerate an established roadmap."],
        ],
      },
      {
        type: "steps",
        eyebrow: "Our Process",
        heading: "A Product Engineering Process Designed for Clarity",
        items: [
          ["Discover", "Define users, workflows, priorities, constraints, and commercial context."],
          ["Architect", "Design application, data, integration, cloud, security, and scaling foundations."],
          ["Plan", "Translate priorities into a realistic phased roadmap."],
          ["Build", "Deliver through iterative product engineering cycles."],
          ["Validate", "Test functionality, usability, reliability, and technical assumptions."],
          ["Launch", "Deploy with appropriate monitoring and operational readiness."],
          ["Evolve", "Improve based on customer feedback, usage, and business priorities."],
        ],
      },
    ],
  },

  "saas-modernization": {
    slug: "saas-modernization",
    eyebrow: "SaaS Modernization",
    h1: "Modernize Your Platform Without Betting the Business on a Full Rewrite",
    intro:
      "Legacy software creates difficult trade-offs. Development slows down. Releases become risky. Infrastructure costs rise. Integrations take longer. Customers expect capabilities the original architecture was never designed to support. Techovarya helps SaaS companies modernize incrementally with a practical, risk-aware engineering strategy.",
    primaryCta: "Request a Modernization Assessment",
    finalCtaHeading: "Get a Practical Modernization Roadmap",
    finalCtaCopy: "We will map your architecture, identify the highest-value improvements, and design a modernization path aligned with your business.",
    finalCta: "Request a Platform Assessment",
    sections: [
      {
        type: "list",
        eyebrow: "Warning Signs",
        heading: "Is Your Platform Becoming a Constraint on Growth?",
        items: [
          "Every release creates unexpected problems.",
          "Critical modules depend on outdated frameworks.",
          "Only a few developers understand important parts of the system.",
          "Performance declines as usage grows.",
          "Cloud costs are difficult to control.",
          "Adding integrations takes too long.",
          "Testing remains heavily manual.",
          "Deployment is stressful.",
          "AI capabilities are difficult to introduce.",
          "Technical debt dominates the roadmap.",
        ],
      },
      {
        type: "list",
        eyebrow: "What We Modernize",
        heading: "Modernization Across the Product Stack",
        items: [
          "Legacy .NET Applications",
          "Node.js Platforms",
          "React Frontends",
          "Monolithic Architectures",
          "Databases",
          "Cloud Infrastructure",
          "APIs",
          "Authentication Systems",
          "Deployment Pipelines",
          "Testing Systems",
          "Observability",
          "Integration Layers",
        ],
      },
      {
        type: "steps",
        eyebrow: "Our Approach",
        heading: "Modernize Where It Creates the Most Value",
        items: [
          ["Assess", "Map architecture, dependencies, bottlenecks, operational risks, and business priorities."],
          ["Prioritize", "Identify the areas where modernization creates the greatest technical and commercial value."],
          ["Stabilize", "Address critical reliability, deployment, performance, or maintainability problems."],
          ["Decouple", "Separate high-change or high-risk components where appropriate."],
          ["Modernize", "Upgrade architecture, infrastructure, applications, and delivery processes incrementally."],
          ["Optimize", "Improve performance, developer velocity, cloud efficiency, reliability, and maintainability."],
        ],
      },
      {
        type: "prose",
        eyebrow: "Rewrite or Modernize?",
        heading: "A Full Rewrite Is Not Always the Answer",
        body: "Rewrites can introduce significant cost, delay, migration risk, and feature parity challenges. We help teams evaluate whether they should refactor, replatform, replace selected components, introduce services gradually, modernize infrastructure, upgrade frameworks, rebuild specific modules, or pursue a complete rewrite. The right answer depends on the business context, not architectural fashion.",
      },
    ],
  },

  "dedicated-product-teams": {
    slug: "dedicated-product-teams",
    eyebrow: "Dedicated Product Teams",
    h1: "Add Engineering Capacity Without Months of Recruiting",
    intro:
      "Your roadmap is growing. Your internal team is stretched. Recruitment takes time. And adding disconnected contractors often creates more coordination than progress. Techovarya provides dedicated engineering capacity aligned with your product, architecture, workflows, and delivery goals.",
    primaryCta: "Build Your Product Team",
    finalCtaHeading: "Build the Engineering Capacity Your Roadmap Requires",
    finalCtaCopy: "Tell us your product, current team shape, and roadmap priorities. We will design a pod that fits.",
    finalCta: "Discuss Your Team Requirements",
    sections: [
      {
        type: "list",
        eyebrow: "When It Fits",
        heading: "Built for Product Teams Facing Capacity Constraints",
        items: [
          "Your roadmap exceeds current engineering capacity.",
          "Recruitment is taking too long.",
          "You need specialized technical capability.",
          "Your internal team needs additional delivery support.",
          "A major initiative requires a focused team.",
          "You need long-term capacity without immediate permanent hiring.",
          "You want a stable team rather than rotating freelancers.",
        ],
      },
      {
        type: "list",
        eyebrow: "Example Pod",
        heading: "A Team Structured Around the Work",
        note: "A typical product pod may include:",
        items: [
          "Senior Full-Stack Engineer",
          "Full-Stack Engineer",
          "Frontend or Backend Specialist",
          "QA Automation Support",
          "Fractional Solution Architect",
          "DevOps Support",
          "Delivery Oversight",
        ],
        footnote:
          "The exact structure depends on the product, roadmap, architecture, and internal team.",
      },
      {
        type: "capabilities",
        eyebrow: "Engagement Models",
        heading: "Flexible Ways to Extend Your Engineering Capability",
        items: [
          ["Dedicated Product Pod", "A stable cross-functional team focused on a defined roadmap."],
          ["Embedded Engineers", "Selected engineers working alongside your internal product team."],
          ["Specialist Capability", "Add focused expertise in AI, cloud, architecture, data, or specific technologies."],
          ["Project-to-Retainer", "Begin with a defined initiative and transition into continuous delivery."],
          ["White-Label Engineering", "Confidential delivery capacity for software agencies and technology consultancies."],
        ],
      },
    ],
  },

  "workflow-automation": {
    slug: "workflow-automation",
    eyebrow: "Workflow Automation",
    h1: "Replace Repetitive Operations With Intelligent Workflows",
    intro:
      "Your teams should not spend valuable time copying information between systems, chasing approvals, manually processing repetitive documents, or coordinating routine work through spreadsheets and email. Techovarya designs and builds connected workflows using APIs, AI, business rules, cloud systems, integrations, and human approvals.",
    primaryCta: "Identify Automation Opportunities",
    finalCtaHeading: "Find the Highest-Value Workflow to Automate",
    finalCtaCopy: "Bring us the workflow that is slowing your business down — we will help you design the right automation.",
    finalCta: "Discuss Your Automation Opportunity",
    sections: [
      {
        type: "list",
        eyebrow: "What We Automate",
        heading: "Turn Repetitive Processes Into Connected Systems",
        items: [
          "Document Processing",
          "Lead Qualification",
          "Sales Operations",
          "Customer Onboarding",
          "Approval Workflows",
          "Reporting",
          "Data Synchronization",
          "Internal Requests",
          "Support Operations",
          "Finance Workflows",
          "Notifications",
          "Compliance Workflows",
          "Operational Coordination",
        ],
      },
      {
        type: "prose",
        eyebrow: "Our Approach",
        heading: "Automate the Workflow, Not Just the Task",
        body: "Many automation projects fail because they optimize one isolated action while ignoring the complete business process. We begin by understanding the full workflow before designing the combination of automation, software, AI, integrations, and human control.",
      },
      {
        type: "list",
        heading: "We Map the Entire Process",
        items: [
          "Who initiates the workflow",
          "Which systems are involved",
          "Where information enters",
          "Where decisions happen",
          "Which steps require human judgment",
          "Where delays occur",
          "What exceptions exist",
          "What should be measured",
        ],
      },
    ],
  },
};

// ---------- Industries ----------
export const INDUSTRIES = {
  "healthcare-technology": {
    slug: "healthcare-technology",
    eyebrow: "Healthcare Technology",
    heroImage:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1200&q=80",
    h1: "Build Healthcare Technology Around Real Workflows, Data, and Users",
    intro:
      "Healthcare software exists inside complex environments involving patients, providers, administrators, operations teams, data systems, and regulatory responsibilities. Techovarya helps healthcare technology companies build connected digital products, operational platforms, intelligent workflows, and data-driven experiences.",
    primaryCta: "Discuss Your HealthTech Product",
    finalCtaHeading: "Build Technology Around the Healthcare Workflow",
    finalCtaCopy: "Share your product context, users, and operational reality. We will help you plan a practical engineering path.",
    finalCta: "Discuss Your Product",
    sections: [
      {
        type: "list",
        eyebrow: "What We Build",
        heading: "Healthcare Software Across the Care Journey",
        items: [
          "Patient-Facing Applications",
          "Provider Platforms",
          "Healthcare Workflow Systems",
          "Administrative Automation",
          "Analytics Platforms",
          "Remote Monitoring Products",
          "Integration Systems",
          "AI-Assisted Workflows",
          "Document Processing",
          "Operational Dashboards",
        ],
      },
      {
        type: "prose",
        eyebrow: "Engineering Context",
        heading: "Healthcare Products Require More Than Feature Delivery",
        body: "A useful healthcare product must account for user roles, workflow complexity, data sensitivity, system integration, reliability, accessibility, and operational reality. We work with product teams to translate those constraints into practical engineering decisions.",
      },
    ],
  },

  "logistics-supply-chain": {
    slug: "logistics-supply-chain",
    eyebrow: "Logistics & Supply Chain",
    heroImage:
      "https://images.pexels.com/photos/906982/pexels-photo-906982.jpeg?auto=compress&cs=tinysrgb&w=1200",
    h1: "Engineer Better Visibility Across Complex Logistics Operations",
    intro:
      "Logistics businesses operate across fragmented systems, time-sensitive workflows, multiple stakeholders, changing conditions, and large volumes of operational information. Techovarya builds software platforms that connect workflows, improve visibility, automate repetitive coordination, and support better operational decisions.",
    primaryCta: "Discuss Your Logistics Platform",
    finalCtaHeading: "Connect the Workflow Behind the Movement",
    finalCtaCopy: "Bring us your operational challenge — we will design the software layer that connects your workflow.",
    finalCta: "Discuss Your Logistics Challenge",
    sections: [
      {
        type: "list",
        eyebrow: "What We Build",
        heading: "Software That Connects Complex Logistics Operations",
        items: [
          "Freight Platforms",
          "Shipment Workflows",
          "Operations Dashboards",
          "Carrier Integrations",
          "Document Automation",
          "IoT Monitoring",
          "Real-Time Notifications",
          "Analytics",
          "Mobile Operations Applications",
          "Customer Portals",
          "Internal Coordination Systems",
        ],
      },
      {
        type: "list",
        eyebrow: "Common Challenges",
        heading: "Where Software Can Remove Operational Friction",
        items: [
          "Information scattered across systems",
          "Manual status updates",
          "Repetitive document handling",
          "Limited shipment visibility",
          "Disconnected stakeholders",
          "Complex exception workflows",
          "Manual reporting",
          "Integration gaps",
          "Slow customer communication",
          "Operational dependence on spreadsheets",
        ],
      },
    ],
  },

  "sales-marketing-technology": {
    slug: "sales-marketing-technology",
    eyebrow: "Sales & Marketing Technology",
    heroImage:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80",
    h1: "Build Intelligent Products for Modern Revenue Teams",
    intro:
      "Sales and marketing products increasingly depend on large data volumes, connected workflows, AI capabilities, search, integrations, personalization, and intuitive user experiences. Techovarya helps SalesTech and MarTech companies engineer intelligent products that turn information into action.",
    primaryCta: "Discuss Your Product",
    finalCtaHeading: "Build the Intelligence Behind Your Revenue Product",
    finalCtaCopy: "Tell us the challenge — from AI search to data pipelines. We will help you design the product engineering path.",
    finalCta: "Discuss Your SalesTech or MarTech Product",
    sections: [
      {
        type: "list",
        eyebrow: "What We Build",
        heading: "Products That Turn Revenue Data Into Action",
        items: [
          "AI Content Platforms",
          "Lead Intelligence Systems",
          "Natural-Language Search",
          "Large-Scale Data Retrieval",
          "CRM Integrations",
          "Workflow Automation",
          "Analytics Dashboards",
          "AI Research Assistants",
          "Content Workflows",
          "Data Enrichment Systems",
        ],
      },
      {
        type: "prose",
        eyebrow: "Product Challenges",
        heading: "Engineering for Data-Intensive Revenue Products",
        body: "Modern revenue technology often requires multiple systems to work together — large datasets, third-party APIs, CRM platforms, search infrastructure, AI models, user permissions, analytics, content workflows, background processing, and automation logic. We help product teams bring these capabilities together into coherent software experiences.",
      },
    ],
  },

  "finance": {
    slug: "finance",
    eyebrow: "Finance & FinTech",
    heroImage:
      "https://images.pexels.com/photos/187041/pexels-photo-187041.jpeg?auto=compress&cs=tinysrgb&w=1200",
    h1: "Engineer Financial Products for Trust, Scale, and Real Workflows",
    intro:
      "Financial products live at the intersection of complex data, regulated processes, real-time expectations, and unforgiving user trust. Techovarya helps banks, lenders, wealth platforms, payments companies, and FinTech founders build software that handles money, decisions, and customer relationships with the precision they require.",
    primaryCta: "Discuss Your FinTech Product",
    finalCtaHeading: "Build Financial Software That Customers Trust",
    finalCtaCopy: "Bring us the product context, regulatory constraints, and workflow reality — we will help design the right engineering path.",
    finalCta: "Discuss Your Finance Product",
    sections: [
      {
        type: "list",
        eyebrow: "What We Build",
        heading: "Financial Software Across the Customer Journey",
        items: [
          "Digital Banking Platforms",
          "Lending & Underwriting Systems",
          "Payments & Wallets",
          "Wealth & Investment Products",
          "KYC & Onboarding Workflows",
          "Trading & Portfolio Dashboards",
          "Compliance & Reporting Systems",
          "Fraud & Risk Analytics",
          "AI-Assisted Advisory Tools",
          "Back-Office Automation",
        ],
      },
      {
        type: "prose",
        eyebrow: "Engineering Context",
        heading: "Financial Products Require Precision, Not Just Features",
        body: "A useful financial product must account for regulated data, permission-aware workflows, precise numerical correctness, latency-sensitive decisions, integration with core banking or payment rails, secure customer identity, and audit-ready operational history. We work with product teams to translate those constraints into practical engineering decisions — and to introduce AI capabilities where they measurably improve customer or operator outcomes.",
      },
      {
        type: "list",
        eyebrow: "Common Challenges",
        heading: "Where Better Engineering Reduces Financial Product Risk",
        items: [
          "Fragmented data across core, ledger, and CRM systems",
          "Slow customer onboarding and KYC",
          "Manual back-office reconciliation",
          "Limited real-time visibility into portfolios or exposure",
          "Difficult regulatory reporting",
          "Legacy platforms constraining product launches",
          "Fraud and risk signals spread across disconnected tools",
          "AI use cases blocked by data access and governance",
        ],
      },
    ],
  },

  "manufacturing": {
    slug: "manufacturing",
    eyebrow: "Manufacturing",
    heroImage:
      "https://images.pexels.com/photos/236722/pexels-photo-236722.jpeg?auto=compress&cs=tinysrgb&w=1200",
    h1: "Connect Operations, Inventory, and Data Across the Factory Floor",
    intro:
      "Manufacturing businesses juggle demand planning, procurement, inventory, production schedules, quality control, dispatch, and finance — often across disconnected tools. Techovarya builds ERP platforms, workflow automation, real-time dashboards, and AI-enabled operations software that turn factory complexity into structured, measurable performance.",
    primaryCta: "Discuss Your Manufacturing Platform",
    finalCtaHeading: "Turn Factory Complexity Into an Operating System",
    finalCtaCopy: "Bring us the operational challenge — inventory, production, quality, or dispatch. We will design a practical engineering path.",
    finalCta: "Discuss Your Manufacturing Challenge",
    sections: [
      {
        type: "list",
        eyebrow: "What We Build",
        heading: "Software That Runs Modern Manufacturing Operations",
        items: [
          "Custom ERP Platforms",
          "Inventory & Stock Management",
          "Production Planning Workflows",
          "CRM & Order Integration",
          "Barcode & Labeling Automation",
          "Warehouse & Dispatch Systems",
          "Quality Control Workflows",
          "Real-Time Operational Dashboards",
          "IoT & Machine Monitoring",
          "AI-Assisted Demand Forecasting",
        ],
      },
      {
        type: "list",
        eyebrow: "Common Challenges",
        heading: "Where Software Removes Friction on the Factory Floor",
        items: [
          "Manual inventory tracking and stock discrepancies",
          "Sales orders disconnected from production schedules",
          "Manual barcode generation and error-prone labeling",
          "Departmental handoffs causing production delays",
          "Limited real-time visibility for management",
          "Aging ERP or accounting software constraining growth",
          "Fragmented reporting across production, finance, and supply chain",
        ],
      },
      {
        type: "prose",
        eyebrow: "Proof",
        heading: "Manufacturing Case Study Example",
        body: "For a car-wash brush manufacturer, we designed and delivered a customized ERP with centralized inventory, CRM-to-production sync, automated barcode generation, standardized workflows, and role-based dashboards — driving a 50% improvement in operational efficiency. Explore the full Gallop Brush Manufacturing case study under Work.",
      },
    ],
  },

  "ecommerce-fulfillment": {
    slug: "ecommerce-fulfillment",
    eyebrow: "E-commerce & Fulfillment",
    heroImage:
      "https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg?auto=compress&cs=tinysrgb&w=1200",
    h1: "Engineer Multi-Channel Commerce and Fulfillment That Scales",
    intro:
      "E-commerce businesses operate across marketplaces, storefronts, warehouses, carriers, and customer expectations that never stop moving. Techovarya builds fulfillment platforms, marketplace integrations, AI-enabled inventory systems, and operational software that turn multi-channel commerce into a coherent operation.",
    primaryCta: "Discuss Your Commerce Platform",
    finalCtaHeading: "Build the Software Layer Behind Every Order",
    finalCtaCopy: "Tell us the operational challenge — from inventory sync to pick-and-pack. We will design the engineering path.",
    finalCta: "Discuss Your E-commerce Challenge",
    sections: [
      {
        type: "list",
        eyebrow: "What We Build",
        heading: "Software for Sellers, Fulfillment Centers, and Marketplaces",
        items: [
          "AI-Powered Fulfillment Platforms",
          "Multi-Marketplace Integrations",
          "Inventory Synchronization Systems",
          "Pick-and-Pack Workflows",
          "Barcode & Scanning Applications",
          "Carrier & Shipping Integrations",
          "Order Management Systems",
          "Warehouse Operations Dashboards",
          "AI Insights & Anomaly Alerts",
          "Customer & Support Portals",
        ],
      },
      {
        type: "list",
        eyebrow: "Common Challenges",
        heading: "Where Better Engineering Unlocks Commerce Growth",
        items: [
          "Fragmented order handling across marketplaces",
          "Manual pick-and-pack slowing throughput",
          "Inventory mismatches and overselling risk",
          "Limited real-time visibility across orders and stock",
          "Onboarding new fulfillment staff takes too long",
          "Reporting across sales channels done in spreadsheets",
          "Difficult to introduce AI without destabilizing operations",
        ],
      },
      {
        type: "prose",
        eyebrow: "Proof",
        heading: "E-commerce Case Study Example",
        body: "For Shipolog, we engineered an AI-powered fulfillment platform that unifies multi-marketplace inventory, automates pick-and-pack, and delivers real-time operational visibility — cutting pick-and-pack times by up to 66% and dramatically reducing picking errors. Explore the full Shipolog case study under Work.",
      },
    ],
  },
};

// ---------- Case Studies ----------
export const CASE_STUDIES = {
  "ai-sales-content-technology": {
    slug: "ai-sales-content-technology",
    industry: "SalesTech / MarTech",
    h1: "AI-Powered Sales & Content Technology",
    summary:
      "Engineering intelligent content, search, and lead-generation workflows for a modern SaaS product.",
    image:
      "https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg",
    capabilities: [
      "AI Product Engineering",
      "Natural-Language Experiences",
      "Search Architecture",
      "SaaS Development",
    ],
    meta: [
      ["Engagement", "Product Initiative"],
      ["Duration", "Multi-quarter build"],
      ["Team", "Cross-functional product pod"],
      ["Stack", "Node.js · React · Vector DB · OpenAI"],
    ],
    challenge:
      "A growing SalesTech platform needed to embed AI capabilities across content generation, prospect research, and intelligent search — without destabilizing an existing revenue-critical product. The team had a working prototype, but response quality, latency, and cost were unpredictable, and the AI layer did not respect existing user permissions or CRM context.",
    approach: [
      ["Retrieval Architecture", "Designed a hybrid retrieval pipeline combining structured CRM data, unstructured content, and permission-aware filters — with source grounding on every response."],
      ["Multi-Model Orchestration", "Built a routing layer that selects the right model per task based on latency, cost, and required quality — with graceful fallback on failure."],
      ["Evaluation Loops", "Created continuous evaluation pipelines using real customer traffic samples, with observability into quality regressions."],
      ["Product Integration", "Embedded AI features inside the existing SaaS UI as first-class product surfaces rather than bolt-on chat widgets."],
    ],
    outcomes: [
      "Faster time to insight for sales users searching account context.",
      "Predictable AI operating cost through model routing and caching.",
      "Higher-quality generated content grounded in customer-specific data.",
      "Foundation for future agentic workflows across the platform.",
    ],
  },

  "logistics-freight-technology": {
    slug: "logistics-freight-technology",
    industry: "Logistics / Freight",
    h1: "Logistics & Freight Technology Platform",
    summary:
      "Building connected software workflows for complex freight and operational processes.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsb2dpc3RpY3MlMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzgyNTcyODU4fDA&ixlib=rb-4.1.0&q=85",
    capabilities: [
      "Cloud Platforms",
      "Workflow Engineering",
      "System Integration",
      "Operational Dashboards",
    ],
    meta: [
      ["Engagement", "Product Initiative + Ongoing Team"],
      ["Duration", "Multi-year platform build"],
      ["Team", "Dedicated Product Pod"],
      ["Stack", ".NET · React · Azure · SQL"],
    ],
    challenge:
      "A freight operator relied on spreadsheets, email threads, and disconnected carrier portals to coordinate time-sensitive shipments. Visibility was limited, exceptions were handled manually, and adding new customers meant onboarding into fragmented workflows.",
    approach: [
      ["Domain Modeling", "Mapped the end-to-end freight workflow across stakeholders, exception paths, and downstream systems before writing production code."],
      ["Integration Layer", "Built a normalized integration surface for carriers, TMS platforms, and customer systems to remove point-to-point brittleness."],
      ["Operational Dashboards", "Delivered real-time views for dispatch, operations, and customer teams — each tailored to the decisions they actually make."],
      ["Incremental Delivery", "Shipped in phased modules so operational teams could adopt without disruption to live business."],
    ],
    outcomes: [
      "Consolidated shipment visibility across previously fragmented systems.",
      "Reduced manual coordination time on routine exceptions.",
      "Faster customer onboarding through a standardized integration layer.",
      "A stable platform ready for further automation and analytics investment.",
    ],
  },

  "iot-monitoring-operational-visibility": {
    slug: "iot-monitoring-operational-visibility",
    industry: "IoT / Monitoring",
    h1: "IoT Monitoring & Operational Visibility",
    summary:
      "Connecting device data, monitoring workflows, and software systems for operational visibility.",
    image:
      "https://images.unsplash.com/photo-1670689334799-cdc6777db8cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwaW90JTIwZmFjdG9yeSUyMGRhcmt8ZW58MHx8fHwxNzgzMjQyNjcyfDA&ixlib=rb-4.1.0&q=85",
    capabilities: [
      "IoT Integration",
      "Monitoring Systems",
      "Backend Engineering",
      "Data Workflows",
    ],
    meta: [
      ["Engagement", "Focused Engineering Sprint → Product Initiative"],
      ["Duration", "Multi-phase engagement"],
      ["Team", "Backend + Data Engineering"],
      ["Stack", "Python · Node.js · AWS · Time-series DB"],
    ],
    challenge:
      "An industrial operator needed a coherent view across a fleet of connected devices — with reliable ingestion, alerting, and dashboards for teams responsible for uptime. Existing tooling was fragmented, and data quality varied by device generation.",
    approach: [
      ["Ingestion Pipeline", "Built a resilient ingestion layer that normalizes device telemetry into a consistent schema, with backpressure and replay support."],
      ["Alerting Rules", "Designed a rule engine for operational alerts — thresholds, patterns, and escalation paths — configurable by domain users."],
      ["Operational Dashboards", "Delivered visibility surfaces for engineering, operations, and leadership — each with the right level of detail."],
      ["Data Workflows", "Established downstream data pipelines that feed analytics and future ML use cases."],
    ],
    outcomes: [
      "Consistent device telemetry across previously fragmented sources.",
      "Reduced mean time to detect operational issues.",
      "A data foundation ready for predictive maintenance workloads.",
      "A monitoring platform teams actually trust and use daily.",
    ],
  },

  "gallop-brush-erp": {
    slug: "gallop-brush-erp",
    industry: "Manufacturing",
    h1: "ERP Implementation for Gallop Brush Manufacturing",
    summary:
      "A customized ERP with inventory, CRM integration, automated barcodes, and real-time dashboards for a growing manufacturer of car-wash brushes and equipment.",
    image:
      "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
    capabilities: [
      "ERP Development",
      "Inventory Management",
      "CRM Integration",
      "Workflow Automation",
      "Operational Dashboards",
    ],
    meta: [
      ["Engagement", "Product Initiative"],
      ["Industry", "Manufacturing"],
      ["Team", "Full-stack + Data Engineering"],
      ["Outcome", "50% operational efficiency gain"],
    ],
    challenge:
      "Before ERP implementation, Gallop Brush faced several operational hurdles: manual inventory tracking led to stock discrepancies and delays; customer orders and sales data were not seamlessly linked with manufacturing schedules, slowing down order fulfillment; barcode generation and labeling were done manually, creating scope for errors; inefficient coordination between departments caused production delays and reduced throughput; and management lacked real-time insights into performance across production, finance, and supply chain.",
    approach: [
      ["Inventory Management System", "Developed a centralized inventory module with automated stock updates and real-time tracking of raw materials, semi-finished, and finished products."],
      ["CRM Integration", "Integrated CRM with ERP for seamless synchronization between sales, orders, and production schedules — enabling automated order-to-production mapping."],
      ["Automated Barcode Creation", "Built a system to automatically generate barcodes for every product batch, integrated with warehouse and dispatch modules to ensure error-free shipping."],
      ["Optimized Product Flow", "Designed automated workflows connecting procurement, production, and dispatch — reducing inter-departmental delays through standardized processes."],
      ["Real-Time Dashboards", "Delivered role-based dashboards for production planners, warehouse teams, sales, and finance — surfacing KPIs like production progress, stock levels, and financial performance."],
    ],
    outcomes: [
      "50% improvement in overall operational efficiency.",
      "Enhanced accuracy in inventory and order-fulfillment processes.",
      "Streamlined product flow, reducing downtime and delays.",
      "Improved collaboration across departments with real-time visibility.",
      "Scalability ensured for future growth and larger production volumes.",
    ],
  },

  "shipolog-ai-fulfillment": {
    slug: "shipolog-ai-fulfillment",
    industry: "E-commerce & Fulfillment",
    h1: "Shipolog — AI-Powered Fulfillment Platform for E-Commerce Sellers",
    summary:
      "An AI-driven fulfillment platform that unifies multi-marketplace inventory, automates pick-and-pack, and delivers real-time operational visibility.",
    image:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
    capabilities: [
      "AI Product Engineering",
      "Multi-Marketplace Integration",
      "Barcode & Scanning",
      "Workflow Automation",
      "Analytics & Reporting",
    ],
    meta: [
      ["Engagement", "Product Initiative"],
      ["Industry", "E-commerce / Fulfillment"],
      ["Team", "Cross-functional Product Pod"],
      ["Outcome", "Up to 66% faster pick-and-pack"],
    ],
    challenge:
      "Sellers across marketplaces like Amazon, Walmart, and eBay juggled disparate systems for tracking, packing, and shipping — leading to errors and inefficiencies. Traditional packing slips slowed operations and increased error rates during high-volume periods. Manual inventory reconciliation across platforms caused overselling and cancellations. Teams lacked centralized, real-time insight across orders, team performance, and inventory.",
    approach: [
      ["AI Product Mapping & Inventory", "Retrieves active listings from connected marketplaces and matches them automatically to physical inventory using SKUs, barcodes, and product metadata. Supports bundle and kit logic, tracks required quantities, and updates stock across all platforms in real time."],
      ["Automated Pick-and-Pack", "Product-based fulfillment workflow eliminates paper packing slips — reducing pick-pack time by up to two-thirds and allowing new hires to start with minimal training."],
      ["Instant Order Lookup", "Any device can scan a barcode to instantly retrieve product and shipping details — dramatically reducing picking and shipping errors."],
      ["Performance Monitoring", "Logs packer activity (who packed what, when) and merges real-time shipment updates from USPS, UPS, and FedEx into a unified operational dashboard."],
      ["AI-Driven Reporting", "Customizable dashboards surface daily sales per marketplace, fulfillment tasks, inventory movement, and P&L. AI-generated alerts flag low stock, underperforming listings, and pricing anomalies."],
    ],
    outcomes: [
      "Pick-and-pack times cut by up to 66% — higher throughput and efficiency.",
      "Barcode lookup and AI mapping drastically reduce picking and shipping errors.",
      "Real-time inventory sync prevents overselling and improves customer satisfaction.",
      "Role-based dashboards empower team-wide decision-making and transparency.",
      "AI-driven automation scales cleanly across multiple channels and order volumes.",
    ],
  },

  "swiggy-payment-reconciliation": {
    slug: "swiggy-payment-reconciliation",
    industry: "Food-Tech Unicorn · FinOps",
    badge: "UNICORN CLIENT",
    featured: true,
    h1: "Swiggy — Automating Payment Reconciliation at Unicorn Scale",
    summary:
      "A production-grade automation pipeline that replaced three days of manual finance work with a real-time reconciliation and data-lake analytics layer across every line of business.",
    image:
      "https://images.pexels.com/photos/265087/pexels-photo-265087.jpeg?auto=compress&cs=tinysrgb&w=1600",
    capabilities: [
      "Workflow Automation",
      "Data Lake Analytics",
      "Payment Reconciliation",
      "Power BI Engineering",
      "Finance Operations",
    ],
    meta: [
      ["Client", "Swiggy — India Food-Tech Unicorn"],
      ["Engagement", "Product Initiative"],
      ["Team", "Full-stack + Data Engineering"],
      ["Outcome", "90 hours saved / month · 22 real-time reports"],
    ],
    challenge:
      "Swiggy's finance team relied on an entirely manual cash-reconciliation process. Each cycle, three team members compiled data from Snowflake, Floating Cash Management Systems, bank statements, and third-party vendor feeds to produce reconciliation summaries covering total cash collected, payment-method breakdowns, cash movements, losses, and balances. Every line of business had its own SOP and cadence — SND and 3PL reconciled weekly, others monthly. Preparing a single report took three days, and the manual nature of the operation produced frequent errors and downstream losses. As Swiggy scaled, the process could not scale with it.",
    approach: [
      [
        "SOP-to-Workflow Translation",
        "Codified every line-of-business SOP into automated workflows using Microsoft Power BI and Power Automate — capturing the operational logic finance had been running manually for years.",
      ],
      [
        "Multi-Source Data Ingestion",
        "Built an ingestion layer pulling data from Snowflake, FCMS, bank statements, third-party vendors, and Google Drive — normalizing everything into a consistent reconciliation model.",
      ],
      [
        "Data Lake Analysis",
        "Structured the aggregated finance data into a lake-style analytical layer, enabling variance detection, cross-LOB queries, and drill-down analysis that were previously impossible on the raw sources.",
      ],
      [
        "Automated Reconciliation & Variance Detection",
        "Each workflow runs the SOP end-to-end — fetch, transform, reconcile, and flag variances — with no human intervention required for the routine path.",
      ],
      [
        "Real-Time Reporting Layer",
        "22 different reports generate in real time — from total payments processed and incomplete transactions to payment dues and cash movements — with a holistic executive view across every LOB.",
      ],
      [
        "Stakeholder Distribution",
        "Reports surface directly in Power BI for finance stakeholders, or auto-export and distribute via email for teams that prefer scheduled delivery.",
      ],
    ],
    outcomes: [
      "90 hours saved per month for the finance team — the equivalent of a full engineer's headcount.",
      "22 different reconciliation reports generated in real time across every line of business.",
      "Reduced turnaround time from three days per cycle to on-demand.",
      "Dramatically improved accuracy — automation removed the manual entry errors that were causing downstream losses.",
      "Enhanced visibility for finance leadership across cash collections, movements, losses, and balances.",
      "A compliance-ready audit trail with data lineage back to source systems.",
      "A scalable analytics foundation that grows with Swiggy's LOB expansion.",
    ],
  },
};

// ---------- About ----------
export const ABOUT = {
  eyebrow: "About Techovarya",
  h1: "We Build the Software Behind Ambitious Businesses",
  intro:
    "For more than a decade, Techovarya has helped founders, technology companies, and growing businesses turn complex requirements into reliable software products. Our work spans SaaS platforms, AI systems, business applications, workflow automation, analytics, mobile products, IoT solutions, and cloud-based software.",
  subIntro:
    "But technology alone is not our purpose. Our role is to help clients make better engineering decisions, reduce product risk, and build systems capable of evolving with the business.",
  story: {
    eyebrow: "Our Story",
    heading: "Built by Engineers. Shaped by Real Product Challenges.",
    body: [
      "Techovarya was built around a straightforward belief: software development should involve more than completing assigned tasks. The best engineering partnerships require context, ownership, technical judgment, communication, and the willingness to think beyond the immediate requirement.",
      "Over the years, we have worked across products, industries, technologies, and business models. That experience has reinforced one principle: the right technical solution begins with understanding the actual problem.",
    ],
  },
  philosophy: {
    eyebrow: "Our Philosophy",
    heading: "How We Think About Engineering",
    items: [
      ["Think Beyond the Ticket", "We seek to understand the business objective behind the requested feature."],
      ["Build for Change", "Products evolve. Architecture should make future change easier, not harder."],
      ["Use AI Where It Creates Value", "We focus on practical AI applications rather than adding AI for presentation."],
      ["Communicate Clearly", "Complex engineering requires transparent communication."],
      ["Take Ownership", "We approach delivery as a partner responsible for progress, not merely assigned tasks."],
      ["Respect Context", "The technically fashionable answer is not always the commercially correct answer."],
    ],
  },
  leadership: {
    eyebrow: "Leadership",
    heading: "Engineering Perspective With Entrepreneurial Context",
    name: "Hemant Jani",
    title: "Founder & CEO",
    bio: "Hemant brings more than 13 years of experience across software engineering, product development, SaaS, cloud systems, AI, and technology entrepreneurship. As both an engineer and business builder, his perspective extends beyond software delivery into product strategy, operational reality, growth constraints, and the commercial decisions behind technology investments. His experience spans modern web platforms, SaaS products, AI-enabled systems, enterprise applications, automation, and the challenges of building and scaling businesses.",
    cta: "Connect With Hemant",
  },
  finalCtaHeading: "Bring Us a Difficult Product Challenge",
  finalCtaCopy: "The best conversations start with the problem, not the solution. Tell us what you are working on.",
  finalCta: "Start a Conversation",
};
